import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { CartItem } from '@/types';
import { detectStorefront } from '@/hooks/useStorefront';

const CHECKOUT_SESSION_KEY = 'bf_checkout_session_id';
const SESSION_KEY = 'bf_session_id';
const IDENTIFIED_EMAIL_KEY = 'bf_omnisend_identified_email';
const PENDING_CART_KEY = 'bf_omnisend_pending_cart';
const PENDING_CHECKOUT_KEY = 'bf_omnisend_pending_checkout';
const PENDING_PRODUCT_VIEWS_KEY = 'bf_omnisend_pending_product_views';
const STORE_URL = 'https://bigfollow.ch';

declare global {
  interface Window {
    omnisend?: {
      identifyContact?: (contact: { email?: string; phone?: string }) => void;
      push?: (args: unknown[]) => void;
    } | unknown[];
  }
}

const sentEvents = new Map<string, number>();
function shouldSend(key: string, windowMs = 5000): boolean {
  const now = Date.now();
  const last = sentEvents.get(key);
  if (last && now - last < windowMs) return false;
  sentEvents.set(key, now);
  return true;
}

// Awaitable resolution of the checkout_sessions row id created by
// trackCheckoutStarted. Field/step updates race with the initial insert
// (user types email before the create RPC returns) — subscribers wait on
// this instead of reading a possibly-empty sessionStorage.
let checkoutSessionIdPromise: Promise<string | null> | null = null;
let resolveCheckoutSessionId: ((v: string | null) => void) | null = null;
function ensureCheckoutSessionIdPromise(): Promise<string | null> {
  if (checkoutSessionIdPromise) return checkoutSessionIdPromise;
  try {
    const existing = sessionStorage.getItem(CHECKOUT_SESSION_KEY);
    if (existing) {
      checkoutSessionIdPromise = Promise.resolve(existing);
      return checkoutSessionIdPromise;
    }
  } catch { /* ignore */ }
  checkoutSessionIdPromise = new Promise<string | null>((resolve) => {
    resolveCheckoutSessionId = resolve;
    // Hard timeout: give up after 8s so callers don't hang forever if the
    // initial insert never fires (e.g. cart empty, checkout not mounted).
    setTimeout(() => resolve(null), 8000);
  });
  return checkoutSessionIdPromise;
}
function publishCheckoutSessionId(id: string) {
  try { sessionStorage.setItem(CHECKOUT_SESSION_KEY, id); } catch { /* ignore */ }
  if (resolveCheckoutSessionId) {
    resolveCheckoutSessionId(id);
    resolveCheckoutSessionId = null;
  }
  checkoutSessionIdPromise = Promise.resolve(id);
}

// Robust email sanitizer: walks every '@' and trims garbage around it.
// Handles: trailing dots, leading/trailing names, concatenated suffixes, case, whitespace.
const COMMON_TLDS = ['online','store','email','tech','site','live','info','shop','xyz','app','dev','com','net','org','edu','gov','biz','ch','de','at','it','fr','io','co','uk','nl','es','pt','se','no','fi','dk','be','pl','cz','ie','ai','me','eu','to','us'];
function sanitizeEmail(email: string): string {
  if (!email) return '';
  const lowered = String(email).toLowerCase().trim();
  for (let i = 0; i < lowered.length; i++) {
    if (lowered[i] !== '@') continue;
    let lstart = i;
    while (lstart > 0 && /[a-z0-9._+-]/.test(lowered[lstart - 1])) lstart--;
    const local = lowered.slice(lstart, i);
    if (!local || /^[._+-]|[._+-]$/.test(local)) continue;
    let rend = i + 1;
    while (rend < lowered.length && /[a-z0-9.-]/.test(lowered[rend])) rend++;
    let domain = lowered.slice(i + 1, rend).replace(/\.+$/, '').replace(/^\.+/, '').replace(/^-+|-+$/g, '');
    if (!domain.includes('.')) continue;
    const parts = domain.split('.');
    let matched = false;
    for (const tld of COMMON_TLDS) {
      const last = parts[parts.length - 1];
      if (last === tld) { matched = true; break; }
      if (last.startsWith(tld) && last.length > tld.length) {
        parts[parts.length - 1] = tld;
        matched = true;
        break;
      }
    }
    if (!matched) continue;
    domain = parts.join('.');
    const candidate = `${local}@${domain}`;
    if (candidate.length > 254) continue;
    return candidate;
  }
  return '';
}

function isValidEmail(email: string | undefined): email is string {
  if (!email) return false;
  return sanitizeEmail(email).length > 0;
}

type OmnisendLineItem = {
  productID: string;
  variantID: string;
  productTitle: string;
  quantity: number;
  productPrice: number;
  currency: string;
  productURL: string;
  productImageURL: string;
};

type PendingCartPayload = {
  cartID: string;
  value: number;
  currency: string;
  abandonedCheckoutURL: string;
  addedItem: {
    productID: string;
    variantID: string;
    productTitle: string;
    quantity: number;
    productPrice: number;
    currency: string;
  };
  lineItems: OmnisendLineItem[];
};

type PendingCheckoutPayload = {
  cartID: string;
  value: number;
  currency: string;
  abandonedCheckoutURL: string;
  lineItems: OmnisendLineItem[];
};

type PendingProductViewPayload = {
  eventVersion: 'v4';
  eventID: string;
  origin: 'api';
  properties: {
    product: {
      id: string;
      currency: string;
      price: number;
      title: string;
      imageUrl: string;
      url: string;
      status: 'inStock';
      categories: Array<{ id: string; title: string }>;
    };
  };
};

function getOrCreateSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const created = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, created);
    return created;
  } catch {
    return crypto.randomUUID();
  }
}

function readSessionValue<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) as T : null;
  } catch {
    return null;
  }
}

function writeSessionValue(key: string, value: unknown) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore storage errors
  }
}

function removeSessionValue(key: string) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore storage errors
  }
}

function rememberIdentifiedEmail(email: string) {
  const sanitized = sanitizeEmail(email);
  try {
    sessionStorage.setItem(IDENTIFIED_EMAIL_KEY, sanitized);
  } catch {
    // ignore storage errors
  }
}

function getTrackedEmail(email?: string): string | undefined {
  const candidate = email ? sanitizeEmail(email) : undefined;
  if (isValidEmail(candidate)) return candidate;

  try {
    const stored = sessionStorage.getItem(IDENTIFIED_EMAIL_KEY) || undefined;
    const sanitized = stored ? sanitizeEmail(stored) : undefined;
    return isValidEmail(sanitized) ? sanitized : undefined;
  } catch {
    return undefined;
  }
}

function identifyOmnisendContact(email?: string, phone?: string) {
  if (typeof window === 'undefined') return;
  const sanitizedEmail = getTrackedEmail(email);
  const omnisendObject = window.omnisend;

  if (!sanitizedEmail && !phone) return;

  try {
    if (omnisendObject && !Array.isArray(omnisendObject) && typeof omnisendObject.identifyContact === 'function') {
      omnisendObject.identifyContact({
        ...(sanitizedEmail ? { email: sanitizedEmail } : {}),
        ...(phone ? { phone } : {}),
      });
      return;
    }
  } catch {
    // fallback to push queue below
  }

  if (Array.isArray(omnisendObject)) {
    omnisendObject.push(['identifyContact', {
      ...(sanitizedEmail ? { email: sanitizedEmail } : {}),
      ...(phone ? { phone } : {}),
    }]);
  }
}

function pushOmnisendSnippetEvent(eventName: string, payload: Record<string, unknown>) {
  if (typeof window === 'undefined') return;
  const omnisendObject = window.omnisend;
  if (Array.isArray(omnisendObject)) {
    omnisendObject.push(['track', eventName, payload]);
  } else if (omnisendObject && typeof omnisendObject === 'object' && 'push' in omnisendObject && typeof omnisendObject.push === 'function') {
    omnisendObject.push(['track', eventName, payload]);
  }
}

async function omnisendAction(action: string, payload: Record<string, unknown>) {
  try {
    const { isOmnisendEnabled } = await import('@/lib/appSettings');
    if (!(await isOmnisendEnabled())) return;
    await supabase.functions.invoke('omnisend-sync', {
      body: { action, ...payload },
    });
  } catch {
    // Non-blocking
  }
}

function buildViewedProductPayload(product: {
  id: string;
  title: string;
  slug: string;
  price: number;
  imageUrl?: string;
  categoryId?: string;
}): PendingProductViewPayload {
  return {
    eventVersion: 'v4',
    eventID: crypto.randomUUID(),
    origin: 'api',
    properties: {
      product: {
        id: product.id,
        currency: 'CHF',
        price: Number(Number(product.price).toFixed(2)),
        title: product.title,
        imageUrl: product.imageUrl || '',
        url: `${STORE_URL}/produkt/${product.slug}`,
        status: 'inStock',
        categories: product.categoryId ? [{ id: product.categoryId, title: product.categoryId }] : [],
      },
    },
  };
}

async function flushPendingOmnisendEvents(email?: string) {
  const resolvedEmail = getTrackedEmail(email);
  if (!resolvedEmail) return;

  identifyOmnisendContact(resolvedEmail);

  const pendingViews = readSessionValue<PendingProductViewPayload[]>(PENDING_PRODUCT_VIEWS_KEY);
  if (pendingViews && pendingViews.length > 0) {
    for (const payload of pendingViews) {
      pushOmnisendSnippetEvent('viewed product', payload);
      // Mirror to server-side so Omnisend's "Viewed Product" workflow triggers
      const product = payload.properties?.product;
      if (product?.id) {
        await omnisendAction('viewed_product', {
          contact: { email: resolvedEmail },
          eventID: payload.eventID,
          product,
        });
      }
    }
    removeSessionValue(PENDING_PRODUCT_VIEWS_KEY);
  }

  const pendingCart = readSessionValue<PendingCartPayload>(PENDING_CART_KEY);
  if (pendingCart) {
    await omnisendAction('cart_add', {
      contact: { email: resolvedEmail },
      ...pendingCart,
    });
    removeSessionValue(PENDING_CART_KEY);
  }

  const pendingCheckout = readSessionValue<PendingCheckoutPayload>(PENDING_CHECKOUT_KEY);
  if (pendingCheckout) {
    setTimeout(() => {
      const email2 = getTrackedEmail();
      if (!email2) return;
      identifyOmnisendContact(email2);
      omnisendAction('checkout_started', {
        contact: { email: email2 },
        ...pendingCheckout,
      });
      removeSessionValue(PENDING_CHECKOUT_KEY);
    }, 60_000);
  }
}

export function useOmnisendTracking() {
  const trackProductView = useCallback((product: {
    id: string;
    title: string;
    slug: string;
    price: number;
    imageUrl?: string;
    categoryId?: string;
  }, email?: string) => {
    if (!shouldSend(`product-view-${product.id}`, 5000)) return;

    const payload = buildViewedProductPayload(product);
    const resolvedEmail = getTrackedEmail(email);

    if (!resolvedEmail) {
      const pending = readSessionValue<PendingProductViewPayload[]>(PENDING_PRODUCT_VIEWS_KEY) || [];
      pending.push(payload);
      if (pending.length > 5) pending.shift();
      writeSessionValue(PENDING_PRODUCT_VIEWS_KEY, pending);
      return;
    }

    identifyOmnisendContact(resolvedEmail);
    pushOmnisendSnippetEvent('viewed product', payload);

    // ALSO send server-side via API so the "Viewed Product" workflow triggers
    // reliably for identified contacts (the Omnisend snippet only fires when the
    // browser-side cookie is in sync, which often fails on returning visitors).
    void omnisendAction('viewed_product', {
      contact: { email: resolvedEmail },
      eventID: payload.eventID,
      product: {
        id: product.id,
        currency: 'CHF',
        price: Number(Number(product.price).toFixed(2)),
        title: product.title,
        imageUrl: product.imageUrl || '',
        url: `${STORE_URL}/produkt/${product.slug}`,
        status: 'inStock',
        categories: product.categoryId ? [{ id: product.categoryId, title: product.categoryId }] : [],
      },
    });
  }, []);

  const trackAddToCart = useCallback((
    item: CartItem,
    allItems: CartItem[],
    cartTotal: number,
    email?: string
  ) => {
    if (!shouldSend(`cart-add-${item.productId}-${item.variationId}`)) return;

    const resolvedEmail = getTrackedEmail(email);
    const cartID = getOrCreateSessionId();

    const lineItems = allItems.map(i => ({
      productID: i.productId,
      variantID: i.variationId,
      productTitle: i.productTitle,
      variationLabel: i.variationLabel || '',
      quantity: 1,
      productPrice: Number(Number(i.price).toFixed(2)),
      currency: 'CHF',
      productURL: `${STORE_URL}/produkt/${i.productId}`,
      productImageURL: i.productImage || '',
    }));

    const payload: PendingCartPayload = {
      cartID,
      value: Number(Number(cartTotal).toFixed(2)),
      currency: 'CHF',
      abandonedCheckoutURL: `${STORE_URL}/checkout`,
      addedItem: {
        productID: item.productId,
        variantID: item.variationId,
        productTitle: item.productTitle,
        quantity: 1,
        productPrice: Number(Number(item.price).toFixed(2)),
        currency: 'CHF',
      },
      lineItems,
    };

    if (!resolvedEmail) {
      writeSessionValue(PENDING_CART_KEY, payload);
      return;
    }

    identifyOmnisendContact(resolvedEmail);
    omnisendAction('cart_add', {
      contact: { email: resolvedEmail },
      ...payload,
    });
  }, []);

  const trackCheckoutStarted = useCallback((
    checkoutItems: CartItem[],
    cartTotal: number,
    email?: string
  ) => {
    if (!shouldSend('checkout-started', 30000)) return;

    const resolvedEmail = getTrackedEmail(email);
    const cartID = getOrCreateSessionId();

    const lineItems = checkoutItems.map(i => ({
      productID: i.productId,
      variantID: i.variationId,
      productTitle: i.productTitle,
      variationLabel: i.variationLabel || '',
      quantity: 1,
      productPrice: Number(Number(i.price).toFixed(2)),
      currency: 'CHF',
      productURL: `${STORE_URL}/produkt/${i.productId}`,
      productImageURL: i.productImage || '',
    }));

    const payload: PendingCheckoutPayload = {
      cartID,
      value: Number(Number(cartTotal).toFixed(2)),
      currency: 'CHF',
      abandonedCheckoutURL: `${STORE_URL}/checkout`,
      lineItems,
    };

    if (!resolvedEmail) {
      writeSessionValue(PENDING_CHECKOUT_KEY, payload);
    } else {
      identifyOmnisendContact(resolvedEmail);
      omnisendAction('checkout_started', {
        contact: { email: resolvedEmail },
        ...payload,
      });
      removeSessionValue(PENDING_CHECKOUT_KEY);
    }

    (supabase.rpc as any)('create_checkout_session', {
      _anonymous_session_id: cartID,
      _email: resolvedEmail || null,
      _cart_value: cartTotal,
      _items_count: checkoutItems.length,
      _line_items_json: lineItems,
      _abandoned_checkout_url: `${STORE_URL}/checkout`,
      _current_step: 'started',
      _storefront: detectStorefront(),
    }).then(({ data, error }: { data: string | null; error: unknown }) => {
      if (data) {
        publishCheckoutSessionId(data);
      }
    });
  }, []);

  const trackContactIdentified = useCallback((contact: {
    email: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    country?: string;
    city?: string;
    postalCode?: string;
    address?: string;
  }) => {
    const sanitizedEmail = sanitizeEmail(contact.email);
    if (!isValidEmail(sanitizedEmail)) return;

    const sanitizedContact = {
      ...contact,
      email: sanitizedEmail,
      phone: contact.phone?.trim() || undefined,
    };

    rememberIdentifiedEmail(sanitizedEmail);
    identifyOmnisendContact(sanitizedEmail, sanitizedContact.phone);

    // Phase 3 – Un-gated Vorname-Persistenz fuer personalisierte Abbrecher-Mails.
    // Rein additiv, non-blocking, unabhaengig von Omnisend.
    const patch: Record<string, string> = {};
    if (sanitizedEmail) patch.email = sanitizedEmail;
    const fn = sanitizedContact.firstName?.trim();
    if (fn) patch.first_name = fn;
    if (Object.keys(patch).length > 0) {
      const anonymousSessionId = getOrCreateSessionId();
      ensureCheckoutSessionIdPromise().then((checkoutSessionId) => {
        if (!checkoutSessionId) return;
        (supabase.rpc as any)('update_checkout_session_public', {
          _id: checkoutSessionId,
          _anonymous_session_id: anonymousSessionId,
          _email: patch.email || null,
          _first_name: patch.first_name || null,
          _current_step: patch.email ? 'email_captured' : null,
        }).then(() => {}, () => {});
      });
    }

    const hasName = !!(sanitizedContact.firstName || sanitizedContact.lastName);
    const dedupeKey = hasName
      ? `contact-${sanitizedEmail}-${sanitizedContact.firstName || ''}-${sanitizedContact.lastName || ''}-${sanitizedContact.city || ''}-${sanitizedContact.postalCode || ''}`
      : `contact-${sanitizedEmail}-noname`;
    const shouldSyncContact = shouldSend(dedupeKey, 30000);
    const syncPromise = shouldSyncContact
      ? omnisendAction('sync_contact', { contact: sanitizedContact })
      : Promise.resolve();

    void syncPromise.then(() => flushPendingOmnisendEvents(sanitizedEmail));
  }, []);

  const trackCheckoutStep = useCallback((step: string, details?: Record<string, unknown>) => {
    if (!shouldSend(`checkout-step-${step}`, 10000)) return;

    const email = typeof details?.email === 'string' ? sanitizeEmail(details.email) : undefined;
    const anonymousSessionId = getOrCreateSessionId();
    ensureCheckoutSessionIdPromise().then((checkoutSessionId) => {
      if (!checkoutSessionId) return;
      (supabase.rpc as any)('update_checkout_session_public', {
        _id: checkoutSessionId,
        _anonymous_session_id: anonymousSessionId,
        _email: isValidEmail(email) ? email : null,
        _first_name: null,
        _current_step: step,
      }).then(() => {}, () => {});
    });

    const criticalSteps = ['email_captured', 'checkout_completed'];
    if (criticalSteps.includes(step) && isValidEmail(email)) {
      const checkoutSessionId = (() => { try { return sessionStorage.getItem(CHECKOUT_SESSION_KEY) || ''; } catch { return ''; } })();
      identifyOmnisendContact(email);
      omnisendAction('send_custom_event', {
        eventName: `bf_checkout_${step}`,
        contact: { email },
        properties: { step, checkoutSessionId },
      });
    }
  }, []);

  const trackCheckoutField = useCallback((
    fieldName: string,
    eventType: string,
    value?: string
  ) => {
    const checkoutSessionId = sessionStorage.getItem(CHECKOUT_SESSION_KEY);
    if (!checkoutSessionId) return;

    const safeFields = ['email', 'first_name', 'last_name', 'country', 'city', 'postal_code', 'payment_method', 'coupon_code'];
    const normalizedValue = safeFields.includes(fieldName) ? (value || '') : '[redacted]';

    supabase.from('checkout_field_events').insert({
      checkout_session_id: checkoutSessionId,
      field_name: fieldName,
      field_group: fieldName.includes('address') || fieldName.includes('city') || fieldName.includes('postal') ? 'address' : 'general',
      event_type: eventType,
      value_redacted_or_normalized: normalizedValue,
    }).then(() => {});
  }, []);

  const trackCustomEvent = useCallback((
    eventName: string,
    properties?: Record<string, unknown>,
    email?: string
  ) => {
    const sanitizedEmail = email ? sanitizeEmail(email) : undefined;
    if (!isValidEmail(sanitizedEmail)) return;
    if (!shouldSend(`custom-${eventName}`, 3000)) return;

    identifyOmnisendContact(sanitizedEmail);
    omnisendAction('send_custom_event', {
      eventName: `bf_${eventName}`,
      contact: { email: sanitizedEmail },
      properties: properties || {},
    });
  }, []);

  return {
    trackProductView,
    trackAddToCart,
    trackCheckoutStarted,
    trackContactIdentified,
    trackCheckoutStep,
    trackCheckoutField,
    trackCustomEvent,
  };
}
